import React, { useEffect, useState, useContext } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCartShopping, faFaceGrinStars, faFaceSadTear, faLock } from '@fortawesome/free-solid-svg-icons'
import CartContext from '../../../middlewares/cart/CartContext'
import AuthContext from '../../../middlewares/AuthContext'
import { useTranslation } from 'react-i18next'
import jwt_decode from 'jwt-decode'
import Swal from 'sweetalert2'
import HomeNav from './HomeNav'
import Footer from './Footer'

// ── Types ─────────────────────────────────────────────────────────────────────
interface CartProduct {
  product_name: string
  name: string
  description: string
  price: number
  price_mxn: number
}

interface CartItem {
  product: CartProduct
  quantity: number
}

interface AuthCtx {
  token: string | null
  getHeaders: () => Promise<Record<string, string>>
}

interface CartCtx {
  cart: CartItem[]
}

// ── Constants ─────────────────────────────────────────────────────────────────
const BRAND        = '#6eb446'
const BRAND_SHADOW = '0 8px 24px rgba(110,180,70,0.3)'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

// ── Skeleton ──────────────────────────────────────────────────────────────────
const SkeletonBlock: React.FC<{ height: number; width?: string }> = ({ height, width = '100%' }) => (
  <div className="shimmer" style={{ height, width, borderRadius: 10 }} />
)

const CheckoutSkeleton: React.FC = () => (
  <>
    <style>{`
      @keyframes shimmer {
        0%   { background-position: -600px 0; }
        100% { background-position:  600px 0; }
      }
      .shimmer {
        background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
        background-size: 600px 100%;
        animation: shimmer 1.4s ease-in-out infinite;
      }
    `}</style>
    <div style={{
      maxWidth: 900, margin: '0 auto', padding: '3rem 1.5rem',
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SkeletonBlock height={28} width="60%" />
        {[1, 2, 3].map(i => <SkeletonBlock key={i} height={72} />)}
        <SkeletonBlock height={44} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SkeletonBlock height={28} width="50%" />
        <SkeletonBlock height={48} />
        <SkeletonBlock height={48} />
      </div>
    </div>
  </>
)

// ── Checkout ──────────────────────────────────────────────────────────────────
const Checkout: React.FC = () => {
  const { token, getHeaders } = useContext(AuthContext) as AuthCtx
  const { cart }              = useContext(CartContext) as CartCtx
  const { t }                 = useTranslation()

  const tokenDecode: any = token !== null ? jwt_decode(token) : null

  const [price, setPrice]         = useState(0)
  const [ready, setReady]         = useState(false)
  const [charging, setCharging]   = useState(false)
  const [email, setEmail]         = useState('')
  const [disabled, setDisabled]   = useState(false)
  const [isInMexico, setIsInMexico] = useState(false)

  useEffect(() => {
    document.body.classList.add('page-home')
    return () => document.body.classList.remove('page-home')
  }, [])

  useEffect(() => {
    if (tokenDecode !== null) {
      if (tokenDecode.roles?.find((r: any) => r.name === 'customer')) {
        setEmail(tokenDecode.email)
        setDisabled(true)
      }
    }
    fetchLocation().then(() => setReady(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    recalcTotal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, isInMexico])

  const fetchLocation = async () => {
    try {
      const res = await axios.get<{ country: string; countryIso: string }>(`${url}api/location`)
      const { country, countryIso } = res.data
      const mexico = country.toLowerCase() === 'mexico' || countryIso.toLowerCase() === 'mx'
      setIsInMexico(mexico)
      return mexico
    } catch {
      return false
    }
  }

  const recalcTotal = () => {
    const total = cart.reduce((acc, item) => {
      const unitPrice = isInMexico
        ? parseFloat(String(item.product.price_mxn))
        : parseFloat(String(item.product.price))
      return acc + item.quantity * unitPrice
    }, 0)
    setPrice(total)
  }

  const checkout = async (params: { email: string }) => {
    setCharging(true)
    try {
      const headers = await getHeaders()
      const res = await axios.post(`${url}payments`, { ...params, products: cart }, { headers })
      if (res.status === 200 && res.data.status === 'success') {
        window.location.href = res.data.url
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.response?.data?.response || 'Error',
        text: err?.response?.data?.message || '',
      })
      setCharging(false)
    }
  }

  const formSchema = Yup.object().shape({
    email: Yup.string().email().required(),
  })

  const currency = isInMexico ? 'MXN' : 'USD'

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '11px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
    boxSizing: 'border-box',
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  if (!ready) return (
    <>
      <HomeNav />
      <CheckoutSkeleton />
    </>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <HomeNav />
      <style>{`
        .co-input:focus { border-color: ${BRAND} !important; box-shadow: 0 0 0 3px rgba(110,180,70,0.12) !important; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Header bar */}
      <div style={{ backgroundColor: '#f8faf6', borderBottom: '1px solid #e2e8f0', padding: '1.25rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <FontAwesomeIcon icon={faCartShopping} style={{ color: BRAND, fontSize: '1.1rem' }} />
          <h1 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>
            {cart.length > 0
              ? <><FontAwesomeIcon icon={faFaceGrinStars} style={{ marginRight: 8, color: BRAND }} />{t('checkout.choice')}</>
              : t('checkout.your_cart')
            }
          </h1>
        </div>
      </div>

      <main style={{ backgroundColor: '#fff', padding: '2.5rem 1.5rem 5rem', flex: 1 }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>

          {cart.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '4rem 1.5rem',
              backgroundColor: '#f8faf6', borderRadius: 20, border: '1px solid #e2e8f0',
            }}>
              <FontAwesomeIcon icon={faFaceSadTear} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
              <p style={{ margin: 0, fontSize: '1.1rem', color: '#64748b', fontWeight: 500 }}>
                No hay artículos en el carrito.
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem',
              alignItems: 'start',
            }}>

              {/* Cart summary */}
              <div style={{
                backgroundColor: '#f8faf6',
                borderRadius: 20,
                padding: '1.5rem',
                border: '1px solid #e8f5dc',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
                    {t('checkout.your_cart')}
                  </h2>
                  <span style={{
                    backgroundColor: BRAND, color: '#fff',
                    borderRadius: 9999, padding: '2px 10px',
                    fontSize: '0.78rem', fontWeight: 700,
                  }}>
                    {cart.reduce((acc, item) => acc + item.quantity, 0)}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {cart.map((item, i) => {
                    const unitPrice = isInMexico
                      ? parseFloat(String(item.product.price_mxn))
                      : parseFloat(String(item.product.price))
                    const lineTotal = (unitPrice * item.quantity).toFixed(2)

                    return (
                      <div key={i} style={{
                        backgroundColor: '#fff',
                        borderRadius: 12,
                        padding: '12px 14px',
                        border: '1px solid #e2e8f0',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>
                              {item.product.product_name}
                            </p>
                            <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                              {item.product.name}
                            </p>
                            <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: '#94a3b8' }}>
                              {item.product.description}
                            </p>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>
                              ${lineTotal} {currency}
                            </p>
                            <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: '#94a3b8' }}>
                              ${unitPrice.toFixed(2)} × {item.quantity}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Total */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #e8f5dc',
                }}>
                  <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
                    Total ({currency})
                  </span>
                  <span style={{ fontWeight: 800, fontSize: '1.15rem', color: BRAND }}>
                    ${parseFloat(String(price)).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Email form */}
              <div style={{
                backgroundColor: '#fff',
                borderRadius: 20,
                padding: '1.75rem',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
              }}>
                <h2 style={{ margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
                  {t('checkout.email_send')}
                </h2>

                {token !== null && tokenDecode?.roles?.find((r: any) => r.name === 'customer') && (
                  <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: BRAND, fontWeight: 600 }}>
                    ✓ Sesión activa: {tokenDecode.email}
                  </p>
                )}

                <Formik
                  initialValues={{ email }}
                  enableReinitialize
                  validationSchema={formSchema}
                  onSubmit={(values) => checkout(values)}
                >
                  <Form noValidate>
                    <div style={{ marginBottom: '1.25rem' }}>
                      <label htmlFor="email" style={{ display: 'block', marginBottom: 6, fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>
                        {t('checkout.email_ob')}
                      </label>
                      <Field
                        id="email" name="email" type="email"
                        placeholder="email@example.com"
                        disabled={disabled}
                        className="co-input"
                        style={{ ...inputStyle, backgroundColor: disabled ? '#f8fafc' : '#fff', opacity: disabled ? 0.8 : 1 }}
                      />
                      <ErrorMessage name="email" render={msg => (
                        <span style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4, display: 'block' }}>{msg}</span>
                      )} />
                    </div>

                    <div style={{ height: 1, backgroundColor: '#f1f5f9', margin: '1.25rem 0' }} />

                    <button
                      type="submit"
                      disabled={charging}
                      style={{
                        width: '100%',
                        padding: '13px',
                        borderRadius: 9999,
                        border: 'none',
                        backgroundColor: charging ? '#a3d08a' : BRAND,
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        cursor: charging ? 'not-allowed' : 'pointer',
                        boxShadow: BRAND_SHADOW,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                      }}
                    >
                      {charging ? (
                        <span style={{
                          width: 16, height: 16, borderRadius: '50%',
                          border: '2px solid rgba(255,255,255,0.4)',
                          borderTopColor: '#fff',
                          display: 'inline-block',
                          animation: 'spin 0.7s linear infinite',
                        }} />
                      ) : (
                        <FontAwesomeIcon icon={faLock} style={{ fontSize: '0.85rem' }} />
                      )}
                      {t('checkout.continue')}
                    </button>
                  </Form>
                </Formik>
              </div>

            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Checkout
